<template>
  <div>
    <form
      @submit.prevent="submitForm"
    >
      <div class="flex flex-wrap justify-content-center">
        <div
          class="w-full lg:w-auto grow border-b lg:border-b-0 w-full static"
        >
          <label class="sr-only" for="omni-field"
            >City, Subdivision, MLS#</label
          >
          <smart-field
            id="omni-field"
            :field-value="formData.omni"
            @updated="updateValue"
            preview="Address, Subdivision, MLS#"
            class="input-wrapper h-12 group static border-b lg:border-b-0 relative z-10"
          ><svg-vue
              icon="map-marker-alt"
              class="text-gray-700 fill-current h-4 w-4 ml-3"
          /></smart-field>
        </div>

        <div
          class="w-full md:w-1/2 lg:w-1/5 shrink border-b lg:border-b-0 lg:border-l"
        >
          <label class="sr-only" for="proptype-field">Area</label>
          <area-field
            id="area-field"
            :fieldValue="formData.area"
            @updated="updateValue"
            preview="Area"
            class="input-wrapper group"
            ><svg-vue
              icon="map"
              class="text-gray-700 fill-current h-5 w-5 ml-3"
          /></area-field>
        </div>

        <div
          class="w-full md:w-1/2 lg:w-1/5 border-b lg:border-b-0 lg:border-l"
        >
          <label class="sr-only" for="proptype-field">Property Type</label>
          <property-type
            id="proptype-field"
            :fieldValue="formData.propertyType"
            @updated="updateValue"
            preview="Property Type"
            class="input-wrapper group"
            ><svg-vue
              icon="home"
              class="text-gray-700 fill-current h-5 w-5 ml-3"
          /></property-type>
        </div>

        <div
          class="w-full lg:w-1/4 shrink"
          :class="{
            'w-full': advancedOpen,
            'w-1/2': !advancedOpen,
          }"
        >
          <div class="w-full flex items-center 2xl:justify-end">
            <div
              class="w-full lg:w-1/2"
            >
              <button
                @click="toggleAdvanced"
                type="button"
                class="form-button text-gray-100/75 hover:text-gray-100/100 bg-primary-dark/50 hover:bg-primary-dark/100 border border-primary-dark flex w-full items-center justify-center px-3"
              >
                Advanced
                <svg-vue
                  v-if="!advancedOpen"
                  icon="angle-down"
                  class="fill-current h-4 w-3 ml-2"
                />
                <svg-vue
                  v-if="advancedOpen"
                  icon="angle-up"
                  class="fill-current h-4 w-3 ml-2"
                />
              </button>
            </div>

            <div
              class="hidden lg:block w-1/2 lg:w-1/2"
              :class="{
                'w-full': advancedOpen,
                'w-1/2': !advancedOpen,
              }"
            >
              <button
                type="submit"
                onclick="return false"
                @click.prevent="submitForm"
                class="form-button bg-accent hover:bg-accent-dark font-bold text-primary-dark w-full flex justify-center items-center px-3"
              >
                Search
                <svg-vue icon="search" class="fill-current h-4 w-4 ml-2" ></svg-vue>
              </button>
            </div>
          </div>
        </div>

        <div class="w-full" v-if="advancedOpen">
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-3"
          >
            <div>
              <label class="sr-only" for="min-price-field">Min Price</label>
              <min-price-field
                :fieldValue="formData.minPrice"
                @updated="updateValue"
                preview="Min Price"
                class="input-wrapper group overflow-hidden"
              >
              </min-price-field>
            </div>
            <div>
              <label class="sr-only" for="max-price-field">Max Price</label>
              <max-price-field
                :fieldValue="formData.maxPrice"
                @updated="updateValue"
                class="input-wrapper group overflow-hidden"
                preview="Max Price"
              ></max-price-field>
            </div>
            <div>
              <label class="sr-only" for="sqft-field">Total Sqft</label>
              <sqft-field
                :fieldValue="formData.sqft"
                @updated="updateValue"
                class="input-wrapper group"
                preview="Total Sqft"
              ></sqft-field>
            </div>

            <div>
              <label class="sr-only" for="bedrooms-field">Bedrooms</label>
              <bedrooms-field
                :fieldValue="formData.beds"
                @updated="updateValue"
                class="input-wrapper group overflow-hidden"
                preview="Bedrooms"
              ></bedrooms-field>
            </div>
            <div>
              <label class="sr-only" for="bathrooms-field">Bathrooms</label>
              <bathrooms-field
                :fieldValue="formData.baths"
                @updated="updateValue"
                class="input-wrapper group overflow-hidden"
                preview="Bathrooms"
              ></bathrooms-field>
            </div>
            <div>
              <label class="sr-only" for="acreage-field">Acreage</label>
              <acreage-field
                :fieldValue="formData.acreage"
                @updated="updateValue"
                class="input-wrapper group overflow-hidden"
                preview="Acreage"
              ></acreage-field>
            </div>

            <div
              class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:col-span-2 lg:col-span-3"
            >
              <div class="bg-gray-300 dark:bg-primary-dark/50 py-4 px-6">
                <status-field
                  :fieldValue="formData.status"
                  @updated="updateValue"
                  class="h-full"
                ></status-field>
              </div>

              <div class="bg-gray-300 dark:bg-primary-dark/50 py-4 px-6">
                <features-field
                  :fieldValue="formData"
                  @updated="updateValue"
                  class="h-full"
                ></features-field>
              </div>

              <div class="bg-gray-300 dark:bg-primary-dark/50 py-4 px-6">
                <waterfront-field
                  :fieldValue="formData.waterfront"
                  @updated="updateValue"
                  class="h-full"
                ></waterfront-field>
              </div>

              <div class="bg-gray-300 dark:bg-primary-dark/50 py-4 px-6">
                <waterview-field
                  :fieldValue="formData.waterview"
                  @updated="updateValue"
                  class="h-full"
                ></waterview-field>
              </div>


            </div>
          </div>
        </div>
        <div
          class="flex lg:hidden"
          :class="{
            'w-full pt-3': advancedOpen,
            'w-1/2': !advancedOpen,
          }"
        >
          <button
            type="submit"
            class="form-button bg-accent hover:bg-accent-dark font-bold text-primary-dark flex w-full items-center justify-center"
          >
            Search
            <svg-vue
              icon="search"
              class="fill-current text-primary-dark h-4 w-4 ml-2"
            />
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Areas from "./fields/Areas.vue";
import Acreage from "./fields/Acreage.vue";
import TotalSqft from "./fields/TotalSqft.vue";
import Bathrooms from "./fields/Bathrooms.vue";
import Bedrooms from "./fields/Bedrooms.vue";
import MaxPrice from "./fields/MaxPrice.vue";
import MinPrice from "./fields/MinPrice.vue";
import Status from "./fields/Status.vue";
import PropertyType from "./fields/PropertyType.vue";
import Features from "./fields/Features.vue";
import Waterfront from "./fields/Waterfront.vue";
import Waterview from "./fields/Waterview.vue";
import SmartField from "./fields/SmartField.vue";

export default {
  name: "PropertySearchForm",

  components: {
    "smart-field": SmartField,
    "area-field": Areas,
    "property-type": PropertyType,
    "status-field": Status,
    "min-price-field": MinPrice,
    "max-price-field": MaxPrice,
    "bedrooms-field": Bedrooms,
    "bathrooms-field": Bathrooms,
    "acreage-field": Acreage,
    "sqft-field": TotalSqft,
    "features-field": Features,
    "waterfront-field": Waterfront,
    "waterview-field": Waterview,
  },

  props: {
    terms: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      advancedOpen: false,
      formData: {
        omni: this.terms.omni ?? "",
        area: this.terms.area ?? "",
        property_type: this.terms.property_type ?? "",
        minPrice: this.terms.minPrice ?? "",
        maxPrice: this.terms.maxPrice ?? "",
        beds: this.terms.beds ?? "",
        baths: this.terms.baths ?? "",
        sqft: this.terms.sqft ?? "",
        acreage: this.terms.acreage ?? "",
        status: this.terms.status ?? "Active|Contingent",
        waterfront: this.terms.waterfront ?? "",
        waterview: this.terms.waterview ?? "",
        waterfront: this.terms.waterfront ?? "",
        sale_type: this.terms.sale_type ?? "",
        pool: this.terms.pool ?? false,
        construction_status: this.terms.construction_status ?? "",
        sort: this.terms.sort ?? "date_listed|desc",
      },
    };
  },

  mounted() {
    if (
      localStorage.advancedOpen === true ||
      !("advancedOpen" in localStorage)
    ) {
      localStorage.advancedOpen = this.advancedOpen;
    } else {
      this.advancedOpen = localStorage.advancedOpen == "false" ? false : true;
    }
  },
  methods: {
    updateValue(data) {
      this.formData[data.property] = data.value ? data.value : null;
    },
    toggleAdvanced(event) {
      if (event) event.preventDefault();
      this.advancedOpen = !this.advancedOpen;
      localStorage.advancedOpen = this.advancedOpen;
    },
    submitForm() {
      this.$emit("submitted", { params: this.formData });

      dataLayer.push({
        'event': 'form_submitted',
        'form_name': 'Property Search',
        'form_type': 'search',
        'form_source': 'Property Search',
        'form_data': this.formData
      })
    },
  },
};
</script>
